import React, { useEffect, useState } from "react";
import {
  EuiFieldText,
  EuiFormRow,
  EuiButton,
  EuiFlexGroup,
  EuiFlexItem,
  EuiSpacer,
  EuiBadge,
  EuiButtonIcon,
  EuiHorizontalRule,
  EuiText,
} from "@elastic/eui";
import { OrgAttributes } from "shared/index";

interface Props {
  initialAttributes?: OrgAttributes;
  onChange?: (attributes: OrgAttributes) => void;
}

export default function OrgAttributesEditor({
  initialAttributes = {},
  onChange,
}: Props) {
  const [attributes, setAttributes] =
    useState<OrgAttributes>(initialAttributes);
  const [newKey, setNewKey] = useState("");
  const [newValue, setNewValue] = useState("");

  useEffect(() => {
    onChange?.(attributes);
  }, [attributes]);

  const addKey = () => {
    const key = newKey.trim();
    if (!key) return;
    if (attributes[key]) {
      setNewKey("");
      return;
    }
    setAttributes((prev) => ({ ...prev, [key]: [] }));
    setNewKey("");
  };

  const removeKey = (key: string) => {
    const copy = { ...attributes };
    delete copy[key];
    setAttributes(copy);
  };

  const addValue = (key: string) => {
    const val = newValue.trim();
    if (!val) return;
    setAttributes((prev) => {
      const arr = prev[key] ?? [];
      if (arr.includes(val)) return prev;
      return { ...prev, [key]: [...arr, val] };
    });
    setNewValue("");
  };

  const removeValue = (key: string, val: string) => {
    setAttributes((prev) => ({
      ...prev,
      [key]: prev[key].filter((v) => v !== val),
    }));
  };

  return (
    <div>
      <EuiFlexGroup gutterSize="s" alignItems="center">
        <EuiFlexItem grow={6}>
          <EuiFormRow label="Neues Attribut (Key)">
            <EuiFieldText
              placeholder="z. B. region"
              value={newKey}
              onChange={(e) => setNewKey(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") addKey();
              }}
            />
          </EuiFormRow>
        </EuiFlexItem>
        <EuiFlexItem grow={false} style={{ alignSelf: "flex-end" }}>
          <EuiButton onClick={addKey} fill>
            Attribut hinzufügen
          </EuiButton>
        </EuiFlexItem>
      </EuiFlexGroup>

      <EuiSpacer />

      {Object.keys(attributes).length === 0 ? (
        <EuiText size="s">Keine Attribute vorhanden.</EuiText>
      ) : (
        Object.entries(attributes).map(([key, values]) => (
          <div key={key} style={{ marginBottom: 12 }}>
            <EuiFlexGroup alignItems="center" gutterSize="s">
              <EuiFlexItem>
                <EuiFormRow label={`Key: ${key}`}>
                  <div>
                    <EuiFlexGroup
                      responsive={false}
                      gutterSize="s"
                      alignItems="center"
                    >
                      <EuiFlexItem grow={true}>
                        {values.length === 0 ? (
                          <EuiText size="s">Keine Werte</EuiText>
                        ) : (
                          <div
                            style={{
                              display: "flex",
                              gap: 8,
                              flexWrap: "wrap",
                            }}
                          >
                            {values.map((v) => (
                              <EuiBadge
                                key={v}
                                iconType="cross"
                                iconOnClick={() => removeValue(key, v)}
                                iconOnClickAriaLabel={`Wert ${v} entfernen`}
                              >
                                {v}
                              </EuiBadge>
                            ))}
                          </div>
                        )}
                      </EuiFlexItem>

                      <EuiFlexItem grow={false} style={{ minWidth: 220 }}>
                        <EuiFlexGroup gutterSize="s">
                          <EuiFlexItem>
                            <EuiFieldText
                              placeholder="Neuen Wert eingeben"
                              value={newValue}
                              onChange={(e) => setNewValue(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") addValue(key);
                              }}
                            />
                          </EuiFlexItem>
                          <EuiFlexItem grow={false}>
                            <EuiButtonIcon
                              aria-label={`Wert hinzufügen`}
                              iconType="plusInCircle"
                              color="success"
                              onClick={() => addValue(key)}
                            />
                          </EuiFlexItem>
                        </EuiFlexGroup>
                      </EuiFlexItem>
                    </EuiFlexGroup>
                  </div>
                </EuiFormRow>
              </EuiFlexItem>

              <EuiFlexItem grow={false}>
                <EuiButtonIcon
                  aria-label={`Attribut ${key} entfernen`}
                  iconType="trash"
                  color="danger"
                  onClick={() => removeKey(key)}
                />
              </EuiFlexItem>
            </EuiFlexGroup>
            <EuiHorizontalRule margin="m" />
          </div>
        ))
      )}

      <EuiSpacer />
    </div>
  );
}
